import { useState } from 'react'
import {
  BookOpen,
  EnvelopeSimple,
  FilePdf,
  MagnifyingGlass,
  PaperPlaneTilt,
} from '@phosphor-icons/react'

import KioskHeader from '../../components/KioskHeader/KioskHeader'
import useKioskAutoHome from '../../hooks/useKioskAutoHome'
import { useLang } from '../../context/LangContext'

const CONTENT = {
  en: {
    pageTitle: 'Resources',
    pageSubtitle:
      'Simple guides, how-to steps, and PDF links sent by email',

    title: 'Resources',
    subtitle:
      'Select a category, read the simple steps, then enter your email to receive the PDF download link.',

    emailLabel: 'Email address',
    emailPlaceholder:
      'Enter your email to receive the PDF link',

    sendLink: 'Send Link',
    success:
      'The PDF link will be sent to your email.',

    categories: [
      {
        id: 'applications',
        title: 'Applications',
        desc:
          'Guides on how to apply for assistance, services, and community support.',
        items: [
          {
            title:
              'How to Apply for Community Assistance',
            desc:
              'Step-by-step guide for submitting an assistance request.',
            steps: [
              'Prepare your name, contact number, and basic request details.',
              'Complete the application form with the required information.',
              'Submit the form together with supporting documents for review.',
            ],
            pdf:
              '/downloads/community-assistance-guide.pdf',
          },
          {
            title:
              'How to Apply for Event Support',
            desc:
              'Information needed when requesting support for community events.',
            steps: [
              'Prepare the event date, location, organiser details, and expected crowd size.',
              'Fill in the event support request form clearly.',
              'Wait for the team to review and contact you for confirmation.',
            ],
            pdf:
              '/downloads/event-support-guide.pdf',
          },
        ],
      },

      {
        id: 'building',
        title: 'Building Information',
        desc:
          'Useful documents about facilities, procedures, and general building guides.',
        items: [
          {
            title: 'Facilities Usage Guide',
            desc:
              'Rules and steps for using shared community facilities.',
            steps: [
              'Check the facility availability and usage requirements.',
              'Prepare your booking details and purpose of use.',
              'Submit the request and follow the approved usage rules.',
            ],
            pdf:
              '/downloads/facilities-usage-guide.pdf',
          },
          {
            title:
              'Maintenance Request Guide',
            desc:
              'Learn what details to prepare before submitting a maintenance ticket.',
            steps: [
              'Identify the issue location and take a clear photo if possible.',
              'Submit a ticket with your contact details and issue description.',
              'The team will review, assign, and update you on the progress.',
            ],
            pdf:
              '/downloads/maintenance-request-guide.pdf',
          },
        ],
      },

      {
        id: 'forms',
        title: 'Forms & Downloads',
        desc:
          'Forms, templates, and supporting documents sent to your email.',
        items: [
          {
            title: 'General Request Form',
            desc:
              'Printable form for general service or support requests.',
            steps: [
              'Read the form requirements before filling in your request.',
              'Complete all required fields and attach supporting documents if needed.',
              'Submit the completed form to the relevant office or person in charge.',
            ],
            pdf:
              '/downloads/general-request-form.pdf',
          },
          {
            title:
              'Visitor Information Form',
            desc:
              'Reference form for visitor or guest-related requests.',
            steps: [
              'Prepare visitor name, contact number, visit date, and purpose of visit.',
              'Fill in the visitor information form completely.',
              'Present or submit the form according to the visitor procedure.',
            ],
            pdf:
              '/downloads/visitor-information-form.pdf',
          },
        ],
      },
    ],
  },

  bm: {
    pageTitle: 'Sumber',
    pageSubtitle:
      'Panduan ringkas, langkah bantuan, dan pautan PDF melalui e-mel',

    title: 'Sumber',
    subtitle:
      'Pilih kategori, baca langkah mudah, kemudian masukkan e-mel anda untuk menerima pautan PDF.',

    emailLabel: 'Alamat e-mel',
    emailPlaceholder:
      'Masukkan e-mel anda untuk menerima pautan PDF',

    sendLink: 'Hantar Pautan',
    success:
      'Pautan PDF akan dihantar ke e-mel anda.',

    categories: [
      {
        id: 'applications',
        title: 'Permohonan',
        desc:
          'Panduan cara memohon bantuan, perkhidmatan, dan sokongan komuniti.',
        items: [
          {
            title:
              'Cara Memohon Bantuan Komuniti',
            desc:
              'Panduan langkah demi langkah untuk menghantar permohonan bantuan.',
            steps: [
              'Sediakan nama, nombor telefon, dan maklumat asas permohonan.',
              'Lengkapkan borang permohonan dengan maklumat yang diperlukan.',
              'Hantar borang bersama dokumen sokongan untuk semakan.',
            ],
            pdf:
              '/downloads/community-assistance-guide.pdf',
          },
          {
            title:
              'Cara Memohon Sokongan Acara',
            desc:
              'Maklumat yang diperlukan untuk permohonan sokongan acara komuniti.',
            steps: [
              'Sediakan tarikh acara, lokasi, maklumat penganjur, dan anggaran jumlah pengunjung.',
              'Isi borang permohonan sokongan acara dengan jelas.',
              'Tunggu pasukan kami menyemak dan menghubungi anda untuk pengesahan.',
            ],
            pdf:
              '/downloads/event-support-guide.pdf',
          },
        ],
      },

      {
        id: 'building',
        title: 'Maklumat Bangunan',
        desc:
          'Dokumen berguna berkaitan fasiliti, prosedur, dan panduan umum bangunan.',
        items: [
          {
            title:
              'Panduan Penggunaan Fasiliti',
            desc:
              'Peraturan dan langkah penggunaan fasiliti komuniti.',
            steps: [
              'Semak ketersediaan fasiliti dan syarat penggunaan.',
              'Sediakan maklumat tempahan dan tujuan penggunaan.',
              'Hantar permohonan dan ikut peraturan penggunaan yang diluluskan.',
            ],
            pdf:
              '/downloads/facilities-usage-guide.pdf',
          },
          {
            title:
              'Panduan Permohonan Penyelenggaraan',
            desc:
              'Ketahui maklumat yang perlu disediakan sebelum menghantar aduan penyelenggaraan.',
            steps: [
              'Kenal pasti lokasi isu dan ambil gambar jika perlu.',
              'Hantar aduan bersama maklumat kontak dan penerangan isu.',
              'Pasukan akan menyemak, menetapkan tindakan, dan mengemas kini status.',
            ],
            pdf:
              '/downloads/maintenance-request-guide.pdf',
          },
        ],
      },

      {
        id: 'forms',
        title: 'Borang & Muat Turun',
        desc:
          'Borang, templat, dan dokumen sokongan yang dihantar melalui e-mel.',
        items: [
          {
            title:
              'Borang Permohonan Umum',
            desc:
              'Borang cetakan untuk permohonan perkhidmatan atau sokongan umum.',
            steps: [
              'Baca syarat borang sebelum mengisi permohonan.',
              'Lengkapkan semua maklumat yang diperlukan dan lampirkan dokumen sokongan jika perlu.',
              'Hantar borang lengkap kepada pejabat atau pegawai berkaitan.',
            ],
            pdf:
              '/downloads/general-request-form.pdf',
          },
          {
            title:
              'Borang Maklumat Pelawat',
            desc:
              'Borang rujukan untuk urusan pelawat atau tetamu.',
            steps: [
              'Sediakan nama pelawat, nombor telefon, tarikh lawatan, dan tujuan lawatan.',
              'Isi borang maklumat pelawat dengan lengkap.',
              'Tunjukkan atau serahkan borang mengikut prosedur pelawat.',
            ],
            pdf:
              '/downloads/visitor-information-form.pdf',
          },
        ],
      },
    ],
  },
}

export default function ResourcesPage() {
  useKioskAutoHome()

  const { lang } = useLang()

  const t = CONTENT[lang] || CONTENT.en

  const [activeCategory, setActiveCategory] =
    useState(t.categories[0].id)

  const [emailByItem, setEmailByItem] =
    useState({})

  const [sentItem, setSentItem] =
    useState('')

  const selectedCategory =
    t.categories.find(
      (category) =>
        category.id === activeCategory,
    ) || t.categories[0]

  const handleEmailChange = (
    itemTitle,
    value,
  ) => {
    setEmailByItem((current) => ({
      ...current,
      [itemTitle]: value,
    }))

    setSentItem('')
  }

  const handleSendLink = (
    event,
    item,
  ) => {
    event.preventDefault()

    const email =
      emailByItem[item.title]?.trim()

    if (!email) return

    // TODO: connect to backend API
    // Example payload:
    // {
    //   email,
    //   title: item.title,
    //   pdf: item.pdf
    // }

    console.log({
      email,
      title: item.title,
      pdf: item.pdf,
    })

    setSentItem(item.title)
  }

  return (
    <main className="kiosk-page">
      <div className="app-container kiosk-container">
        <KioskHeader
          title={t.pageTitle}
          subtitle={t.pageSubtitle}
        />

        <section className="kiosk-section-card resources-page">
          <div className="kiosk-section-card__header">
            <BookOpen size={30} />

            <div>
              <h1>{t.title}</h1>
              <p>{t.subtitle}</p>
            </div>
          </div>

          <div className="resources-layout">
            <aside
              className="resources-category-list"
              aria-label="Resource categories"
            >
              {t.categories.map(
                (category) => (
                  <button
                    key={category.id}
                    type="button"
                    className={`resources-category-card${
                      category.id ===
                      activeCategory
                        ? ' is-active'
                        : ''
                    }`}
                    onClick={() =>
                      setActiveCategory(
                        category.id,
                      )
                    }
                  >
                    <span>
                      <MagnifyingGlass size={22} />
                    </span>

                    <strong>
                      {category.title}
                    </strong>

                    <small>
                      {category.desc}
                    </small>
                  </button>
                ),
              )}
            </aside>

            <div className="resources-content-panel">
              <div className="resources-content-panel__header">
                <div>
                  <h2>
                    {selectedCategory.title}
                  </h2>

                  <p>
                    {selectedCategory.desc}
                  </p>
                </div>
              </div>

              <div className="resources-download-list">
                {selectedCategory.items.map(
                  (item) => (
                    <article
                      className="resources-download-card"
                      key={item.title}
                    >
                      <div className="resources-download-card__top">
                        <span className="resources-download-card__icon">
                          <FilePdf size={30} />
                        </span>

                        <div>
                          <strong>
                            {item.title}
                          </strong>

                          <p>{item.desc}</p>
                        </div>
                      </div>

                      <div className="resources-steps">
                        {item.steps.map(
                          (
                            step,
                            index,
                          ) => (
                            <div
                              className="resources-step"
                              key={step}
                            >
                              <span>
                                {index + 1}
                              </span>

                              <p>{step}</p>
                            </div>
                          ),
                        )}
                      </div>

                      <form
                        className="resources-email-form"
                        onSubmit={(
                          event,
                        ) =>
                          handleSendLink(
                            event,
                            item,
                          )
                        }
                      >
                        <label
                          htmlFor={`email-${item.title
                            .replace(
                              /\s+/g,
                              '-',
                            )
                            .toLowerCase()}`}
                        >
                          {t.emailLabel}
                        </label>

                        <div className="resources-email-form__row">
                          <div className="resources-email-input">
                            <EnvelopeSimple size={18} />

                            <input
                              id={`email-${item.title
                                .replace(
                                  /\s+/g,
                                  '-',
                                )
                                .toLowerCase()}`}
                              type="email"
                              inputMode="email"
                              placeholder={
                                t.emailPlaceholder
                              }
                              value={
                                emailByItem[
                                  item.title
                                ] || ''
                              }
                              onChange={(
                                event,
                              ) =>
                                handleEmailChange(
                                  item.title,
                                  event.target
                                    .value,
                                )
                              }
                              required
                            />
                          </div>

                          <button
                            className="btn btn--primary"
                            type="submit"
                          >
                            <PaperPlaneTilt size={18} />
                            {t.sendLink}
                          </button>
                        </div>

                        {sentItem ===
                          item.title && (
                          <p className="resources-email-form__success">
                            {t.success}
                          </p>
                        )}
                      </form>
                    </article>
                  ),
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  )
}